import { I18n } from "react-redux-i18n";
import React from "react";

import { FolderPrimaryListDto } from "@common/dto/folder/primary-list-dto";
import { generateId } from "@common/utils/common";
import {
  FieldQueryColumn,
  FieldQueryColumnComponentRenderParams,
  FieldQueryItem,
} from "@common/components/field-query/field-query.types";
import { FieldPrimaryListDto } from "@common/dto/field/list-primary-dto";
import { Flex } from "@common/components/Flex";
import { Ellipsis } from "@common/components/Ellipsis";
import { Button } from "@common/components/Button";
import { getTestAttributes } from "@common/utils/tests";
import { MessageConfirmParams } from "@common/hooks/message";

import { SpreadsheetManagementDraggedField, InternalFieldPrimaryList } from "./spreadsheet-management.types";
import { SPREADSHEET_MODULE_NAME } from "../../constants/shared";
import { groupTableFieldsByParent } from "./columns-grouping.utils";

type ColumnsTableControllerProps = {
  selectedFields: InternalFieldPrimaryList[];
  selectedFolder?: FolderPrimaryListDto;
  onFieldRemoveClick: (removedInternalFieldId: SpreadsheetManagementDraggedField["id"]) => void;
  onFieldSortClick: (field: SpreadsheetManagementDraggedField) => void;
  onFieldAddQueryClick: (field: SpreadsheetManagementDraggedField) => void;
  onFieldsRemoveClick: () => void;
  showConfirmMessage: (props: MessageConfirmParams) => void;
};

export const useColumnsTableController = ({
  selectedFields,
  selectedFolder,
  onFieldRemoveClick,
  onFieldsRemoveClick,
  onFieldSortClick,
  onFieldAddQueryClick,
  showConfirmMessage,
}: ColumnsTableControllerProps) => {
  const [columnsTableFields, setColumnsTableFields] = React.useState<Array<SpreadsheetManagementDraggedField>>([]);

  const handleColumnsTableFieldDrop = React.useCallback(() => {
    const data: Array<SpreadsheetManagementDraggedField> = selectedFields
      .filter((folderField) => {
        const hasDuplicate = columnsTableFields.some((column) => {
          const sameIdAndSystem = column.field.id === folderField.id && column.field.system === folderField.system;
          
          if (!sameIdAndSystem) {
            return false;
          }
          
          const columnParent = column.parentField;
          const folderParent = folderField.parentField;

          if (columnParent === null && folderParent === null) {
            return true;
          }
          if ((columnParent === null && folderParent !== null) || (columnParent !== null && folderParent === null)) {
            return false;
          }
          if (columnParent !== null && columnParent !== undefined && folderParent !== null && folderParent !== undefined) {
          return columnParent.id === folderParent.id;
        }
          
          return false;
        });
        
        return !hasDuplicate;
      })
      .map((folderField) => ({
        id: generateId().toString(),
        field: folderField,
        folder: selectedFolder!,
        parentField: folderField?.parentField,
        editable: folderField.editable,
        lookupValues: folderField.lookupValues,
      }));
  
      setColumnsTableFields((prevFields) =>
        groupTableFieldsByParent([...prevFields, ...data], {
          pinNonParentToTop: true,
        })
      );
  }, [selectedFields, selectedFolder, columnsTableFields]);

  const columnActions = React.useMemo(
    () => [
      {
        id: getTestAttributes(SPREADSHEET_MODULE_NAME, "field-query-columns-table-sort", "button").id,
        iconSrc: "png/sort.png",
        tooltip: I18n.t("Add to Sort"),
        onClick: (internalFieldId: SpreadsheetManagementDraggedField["id"]) => {
          const field = columnsTableFields.find((field) => field.id === internalFieldId);

          if (field) {
            onFieldSortClick(field);
          }
        },
      },
      {
        id: getTestAttributes(SPREADSHEET_MODULE_NAME, "field-query-columns-table-query-add", "button").id,
        iconSrc: "png/query_add.png",
        tooltip: I18n.t("Add to Query"),
        onClick: (internalFieldId: FieldQueryItem["id"]) => {
          const field = columnsTableFields.find((field) => field.id === internalFieldId);

          if (field) {
            onFieldAddQueryClick(field);
          }
        },
      },
      {
        id: getTestAttributes(SPREADSHEET_MODULE_NAME, "field-query-columns-table-cancel", "button").id,
        iconSrc: "gif/cancel.gif",
        tooltip: I18n.t("Remove Field"),
        onClick: (removedInternalFieldId: FieldQueryItem["id"]) => {
          setColumnsTableFields((currentFields) =>
            currentFields.filter((field) => field.id !== removedInternalFieldId)
          );
          onFieldRemoveClick(removedInternalFieldId);
        },
        
      },
    ],
    [onFieldRemoveClick, onFieldSortClick, onFieldAddQueryClick, columnsTableFields]
  );

  const columnsTable: Array<FieldQueryColumn> = React.useMemo(
    () => [
      {
        id: "index",
        component: ({ rowIndex }: FieldQueryColumnComponentRenderParams<SpreadsheetManagementDraggedField>) => {
          return rowIndex + 1;
        },
      },
      {
        label: I18n.t("Type"),
        id: "type",
        component: ({ row }: FieldQueryColumnComponentRenderParams<SpreadsheetManagementDraggedField>) => {
          return <img src={row.field.imageURL} alt={row.field.imageURL} />;
        },
      },
      {
        label: I18n.t("Field"),
        id: "field",
        component: ({ row }: FieldQueryColumnComponentRenderParams<SpreadsheetManagementDraggedField>) => {
          return (
            <Flex alignItems="center" justify="space-between">
              <Ellipsis
                maxWidth={300}
                text={row.parentField ? `${row.parentField?.name??row.parentField?.fieldname}.${row.field.name}` : row.field.name}
                className="SpreadsheetDetailsSpreadsheetManagement__content__management__builder__column__title"
              />
              <Flex className="SpreadsheetDetailsSpreadsheetManagement__content__management__builder__column__actions">
                {columnActions.map((action) => (
                  <Button
                    id={action.id}
                    key={action.id}
                    className="SpreadsheetDetailsSpreadsheetManagement__content__management__builder__column__action"
                    onClick={() => action.onClick(row.id)}
                    variant="text"
                    title={action.tooltip}
                    aria-label={action.tooltip}
                  >
                    <img src={action.iconSrc} height="100%" alt={action.tooltip} />
                  </Button>
                ))}
              </Flex>
            </Flex>
          );
        },
      },
      {
        label: I18n.t("Path"),
        id: "path",
        component: ({
          row,
        }: FieldQueryColumnComponentRenderParams<{ field: FieldPrimaryListDto; folder: FolderPrimaryListDto }>) => {
          
          if(row.field?.system === true){
            return "System Fields";
          }

          // Remove field name from the end of the folder path
          const fullPath = row.field.fullPath || '';
          const lastBackslashIndex = fullPath.lastIndexOf('\\');
          return lastBackslashIndex !== -1 ? fullPath.substring(0, lastBackslashIndex) : fullPath;
        },
      },
    ],
    [columnActions]
  );

  const handleColumnsTableFieldsRemove = React.useCallback(() => {
    showConfirmMessage({
      cancelText: I18n.t("No"),
      confirmText: I18n.t("Yes"),
      confirmBtnId: "btn-message-box-ok",
      cancelBtnId: "btn-message-box-dont-cancel",
      content: I18n.t("Are you sure you wish to remove all fields?"),
      onCustomOk: () => {
        setColumnsTableFields([]);
        onFieldsRemoveClick();
      },
    });
  }, [showConfirmMessage, onFieldsRemoveClick]);

  return {
    columnsTable,
    columnsTableFields,
    setColumnsTableFields,
    handleColumnsTableFieldDrop,
    handleColumnsTableFieldsRemove,
  };
};
